import { joinRoom, type JoinError, type Room, type TargetPeers } from "trystero";
import type { TodoSnapshot } from "./shared/todos";
import { decryptJsonWithSecret, type EncryptedJsonPayload, encryptJsonWithSecret } from "./crypto";

export type SyncClientConfig = {
  appId: string;
  relayUrls: string[];
};

export type SyncClientCallbacks = {
  getDeviceId: () => string;
  getSnapshot: () => TodoSnapshot | null;
  onRemoteSnapshot: (vaultId: string, snapshot: TodoSnapshot) => void;
  onPeerCountChange: (count: number) => void;
  onStatusChange: (status: "idle" | "connecting" | "connected" | "error", error?: string) => void;
  onSyncComplete: () => void;
};

type ActiveVault = {
  id: string;
  password: string;
};

type SnapshotRequestEnvelope = {
  type: "snapshot-request";
  deviceId: string;
  timestamp: number;
  vaultId: string;
};

type SnapshotEnvelope = {
  type: "snapshot";
  deviceId: string;
  payload: EncryptedJsonPayload;
  timestamp: number;
  vaultId: string;
};

const SNAPSHOT_REQUEST_ACTION = "taskguard.snapshot-request.v1";
const SNAPSHOT_ACTION = "taskguard.snapshot.v1";
const DEFAULT_SYNC_APP_ID = "taskguard.nostr.sync.v1";
const CONFIG_URL = "./public/taskguard.config.json";

type RuntimeSyncConfig = {
  appId?: string;
  relayUrls?: string | string[];
};

function normaliseRelayUrl(value: string) {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return null;
  }

  try {
    const url = new URL(trimmed);

    if (url.protocol !== "ws:" && url.protocol !== "wss:") {
      return null;
    }

    return url.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
}

function normaliseRelayUrls(value: unknown) {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .map((item) => normaliseRelayUrl(item))
      .filter((item): item is string => item != null);
  }

  if (typeof value !== "string") {
    return [];
  }

  return value
    .split(",")
    .map((item) => normaliseRelayUrl(item))
    .filter((item): item is string => item != null);
}

function inferRelayUrl() {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const hostname = window.location.hostname || "localhost";
  return `${protocol}://${hostname}:9091`;
}

async function loadRuntimeConfig(): Promise<RuntimeSyncConfig | null> {
  try {
    const response = await fetch(CONFIG_URL, {
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as RuntimeSyncConfig;
  } catch {
    return null;
  }
}

function resolveConfig(runtimeConfig: RuntimeSyncConfig | null): SyncClientConfig {
  const relayUrls = Array.from(new Set(normaliseRelayUrls(runtimeConfig?.relayUrls)));
  const appId =
    typeof runtimeConfig?.appId === "string" && runtimeConfig.appId.trim().length > 0
      ? runtimeConfig.appId.trim()
      : DEFAULT_SYNC_APP_ID;

  return {
    appId,
    relayUrls: relayUrls.length > 0 ? relayUrls : [inferRelayUrl()],
  };
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export class SyncClient {
  private readonly callbacks: SyncClientCallbacks;
  private readonly config: SyncClientConfig;
  private closed = false;
  private activeVault: ActiveVault | null = null;
  private room: Room | null = null;
  private roomVersion = 0;
  private snapshotRetryTimer: number | undefined;
  private sendSnapshotRequest: ((data: SnapshotRequestEnvelope, targetPeers?: TargetPeers) => Promise<void[]>) | null = null;
  private sendSnapshot: ((data: SnapshotEnvelope, targetPeers?: TargetPeers) => Promise<void[]>) | null = null;

  private constructor(callbacks: SyncClientCallbacks, config: SyncClientConfig) {
    this.callbacks = callbacks;
    this.config = config;
  }

  static async create(callbacks: SyncClientCallbacks): Promise<SyncClient> {
    callbacks.onStatusChange("connecting");

    const runtimeConfig = await loadRuntimeConfig();
    const config = resolveConfig(runtimeConfig);
    const client = new SyncClient(callbacks, config);

    callbacks.onPeerCountChange(0);
    callbacks.onStatusChange("idle");
    return client;
  }

  async setVault(vault: ActiveVault | null) {
    if (this.closed) {
      return;
    }

    const previousVault = this.activeVault;
    const isSameVault = previousVault?.id === vault?.id && previousVault?.password === vault?.password;

    if (isSameVault) {
      return;
    }

    await this.leaveRoom();
    this.activeVault = vault;

    if (vault == null) {
      this.callbacks.onPeerCountChange(0);
      this.callbacks.onStatusChange("idle");
      return;
    }

    this.callbacks.onPeerCountChange(0);
    this.callbacks.onStatusChange("connecting");

    const roomVersion = this.roomVersion + 1;
    this.roomVersion = roomVersion;

    const room = joinRoom(
      {
        appId: this.config.appId,
        password: vault.password,
        relayUrls: this.config.relayUrls,
      },
      vault.id,
      {
        onJoinError: (details) => {
          if (!this.isActiveRoom(roomVersion, vault.id)) {
            return;
          }

          this.handleJoinError(details);
        },
      },
    );

    const [sendSnapshotRequest, getSnapshotRequest] = room.makeAction<SnapshotRequestEnvelope>(SNAPSHOT_REQUEST_ACTION);
    const [sendSnapshot, getSnapshot] = room.makeAction<SnapshotEnvelope>(SNAPSHOT_ACTION);

    this.room = room;
    this.sendSnapshotRequest = sendSnapshotRequest;
    this.sendSnapshot = sendSnapshot;

    room.onPeerJoin((peerId) => {
      if (!this.isActiveRoom(roomVersion, vault.id)) {
        return;
      }

      this.updatePeerCount();
      this.callbacks.onStatusChange("connected");
      void this.requestSnapshot(peerId);
    });

    room.onPeerLeave(() => {
      if (!this.isActiveRoom(roomVersion, vault.id)) {
        return;
      }

      this.updatePeerCount();
    });

    getSnapshotRequest((envelope, peerId) => {
      if (!this.isActiveRoom(roomVersion, vault.id) || envelope.vaultId !== vault.id) {
        return;
      }

      if (envelope.deviceId === this.callbacks.getDeviceId()) {
        return;
      }

      void this.broadcastSnapshot(peerId);
    });

    getSnapshot((envelope) => {
      if (!this.isActiveRoom(roomVersion, vault.id) || envelope.vaultId !== vault.id) {
        return;
      }

      if (envelope.deviceId === this.callbacks.getDeviceId()) {
        return;
      }

      void this.handleSnapshot(envelope, vault.password);
    });

    this.updatePeerCount();
    this.callbacks.onStatusChange("connected");
    await this.requestSnapshot();
    await this.broadcastSnapshot();
    this.scheduleSnapshotRetry();
  }

  async broadcastSnapshot(targetPeers?: TargetPeers) {
    if (this.activeVault == null || this.sendSnapshot == null) {
      return;
    }

    const snapshot = this.callbacks.getSnapshot();

    if (snapshot == null) {
      return;
    }

    const payload = await encryptJsonWithSecret(snapshot, this.activeVault.password);
    await this.sendSnapshot(
      {
        type: "snapshot",
        vaultId: this.activeVault.id,
        deviceId: this.callbacks.getDeviceId(),
        payload,
        timestamp: Date.now(),
      },
      targetPeers,
    );
  }

  async stop() {
    if (this.closed) {
      return;
    }

    this.closed = true;
    await this.leaveRoom();
    this.activeVault = null;
    this.callbacks.onPeerCountChange(0);
    this.callbacks.onStatusChange("idle");
  }

  private async requestSnapshot(targetPeers?: TargetPeers) {
    if (this.activeVault == null || this.sendSnapshotRequest == null) {
      return;
    }

    await this.sendSnapshotRequest(
      {
        type: "snapshot-request",
        vaultId: this.activeVault.id,
        deviceId: this.callbacks.getDeviceId(),
        timestamp: Date.now(),
      },
      targetPeers,
    );
  }

  private async handleSnapshot(envelope: SnapshotEnvelope, password: string) {
    try {
      const snapshot = await decryptJsonWithSecret<TodoSnapshot>(envelope.payload, password);
      this.callbacks.onRemoteSnapshot(envelope.vaultId, snapshot);
      this.callbacks.onSyncComplete();
      this.callbacks.onStatusChange("connected");
      this.updatePeerCount();
    } catch {
      this.callbacks.onStatusChange("error", "Received an unreadable vault update.");
    }
  }

  private handleJoinError(details: JoinError) {
    this.callbacks.onStatusChange("error", details.error || "Vault sync could not join the Trystero room.");
  }

  private isActiveRoom(roomVersion: number, vaultId: string) {
    return !this.closed && this.roomVersion === roomVersion && this.activeVault?.id === vaultId && this.room != null;
  }

  private updatePeerCount() {
    this.callbacks.onPeerCountChange(Object.keys(this.room?.getPeers() ?? {}).length);
  }

  private async leaveRoom() {
    if (this.snapshotRetryTimer != null) {
      window.clearTimeout(this.snapshotRetryTimer);
      this.snapshotRetryTimer = undefined;
    }

    const room = this.room;

    this.room = null;
    this.sendSnapshot = null;
    this.sendSnapshotRequest = null;
    this.roomVersion += 1;

    if (room != null) {
      try {
        await room.leave();
      } catch (error) {
        console.error("Unable to leave Trystero room", error);
      }
    }
  }

  private scheduleSnapshotRetry() {
    this.snapshotRetryTimer = window.setTimeout(() => {
      this.snapshotRetryTimer = undefined;

      void this.requestSnapshot().catch((error) => {
        this.callbacks.onStatusChange("error", getErrorMessage(error, "Vault sync could not request a snapshot."));
      });
    }, 1_000);
  }
}
